import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { getBrand } from '../config/brands';

const LandingContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${props => props.theme.secondaryColor} 0%, #c3cfe2 100%);
`;

const Header = styled.header`
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1rem 0;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  
  .logo {
    font-size: 1.5rem;
    font-weight: bold;
    color: ${props => props.theme.primaryColor};
    cursor: pointer;
    text-decoration: none;
    transition: opacity 0.2s;
    
    &:hover {
      opacity: 0.8;
    }
  }
`;

const HeroWrapper = styled.div`
  position: relative;
  background-image: url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1920&q=80');
  background-size: cover;
  background-position: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      ${props => props.theme.secondaryColor}ee 0%,
      rgba(255,255,255,0.82) 100%
    );
  }
`;

const HeroSection = styled.section`
  position: relative;
  padding: 5rem 2rem;
  text-align: center;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroLogo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;

  img {
    height: 80px;
    max-width: 300px;
    object-fit: contain;

    @media (max-width: 768px) {
      height: 60px;
      max-width: 250px;
    }
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: #6b7280;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CtaButton = styled.button`
  background: ${props => props.theme.primaryColor};
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.125rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.theme.primaryColor}dd;
    transform: translateY(-2px);
  }
`;

const FeaturesSection = styled.section`
  padding: 4rem 2rem;
  background: white;
`;

const FeaturesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const FeaturesTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 3rem;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const FeatureCard = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;

  .feature-img {
    width: 100%;
    height: 160px;
    object-fit: cover;
    display: block;
  }

  .feature-icon {
    font-size: 2rem;
    color: ${props => props.theme.primaryColor};
    margin-bottom: 1rem;
  }

  .feature-body {
    padding: 1.5rem;
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }

  p {
    color: #6b7280;
    line-height: 1.6;
    font-size: 0.9rem;
  }
`;

const ComplianceSection = styled.section`
  padding: 4rem 2rem;
  background: #0f0f1a;
  color: #fff;
`;

const ComplianceContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ComplianceLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #4f8ef7;
  margin-bottom: 16px;
`;

const ComplianceTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1rem;
  line-height: 1.25;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ComplianceDesc = styled.p`
  font-size: 1rem;
  color: rgba(255,255,255,0.6);
  line-height: 1.75;
  margin-bottom: 2rem;
`;

const CompliancePoints = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CompliancePoint = styled.li`
  font-size: 0.9rem;
  color: rgba(255,255,255,0.7);
  padding-left: 20px;
  position: relative;
  line-height: 1.5;

  &::before {
    content: '→';
    position: absolute;
    left: 0;
    color: #4f8ef7;
  }
`;

const ComplianceButton = styled.button`
  background: #fff;
  color: #0f0f1a;
  border: none;
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const ComplianceVisual = styled.div`
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 16px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WeekPill = styled.div<{ active?: boolean }>`
  background: ${p => p.active ? 'rgba(79,142,247,0.15)' : 'rgba(255,255,255,0.04)'};
  border: 1px solid ${p => p.active ? 'rgba(79,142,247,0.4)' : 'rgba(255,255,255,0.08)'};
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 13px;
  color: ${p => p.active ? '#fff' : 'rgba(255,255,255,0.4)'};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const WeekBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #4f8ef7;
  background: rgba(79,142,247,0.15);
  padding: 2px 8px;
  border-radius: 20px;
`;

const FooterSection = styled.footer`
  background: #f5f5f5;
  color: #4b5563;
  padding: 2rem;
  text-align: center;
`;

interface LandingPageProps {}

const LandingPage: React.FC<LandingPageProps> = () => {
  const { brand: brandParam } = useParams<{ brand?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isRoot = location.pathname === '/' || location.pathname === '/home';
  const brand = getBrand(isRoot ? undefined : brandParam);

  const handleGetStarted = () => {
    const registerPath = brandParam 
      ? `/register?ref=${brandParam}` 
      : '/register';
    navigate(registerPath);
  };

  const handleLogoClick = () => {
    const landingPath = brandParam 
      ? `/${brandParam}` 
      : '/';
    window.location.href = `https://finaconnect.com${landingPath}`;
  };

  const theme = {
    primaryColor: brand.primaryColor,
    secondaryColor: brand.secondaryColor
  };

  return (
    <ThemeProvider theme={theme}>
      <LandingContainer>
        {brandParam !== 'tgpay' && (
          <Header>
            <HeaderContent>
              <div className="logo" onClick={handleLogoClick}>
                {brand.logo ? (
                  <img src={brand.logo} alt={brand.name} style={{ height: '40px', maxWidth: '200px', objectFit: 'contain' }} />
                ) : (
                  brand.name
                )}
              </div>
            </HeaderContent>
          </Header>
        )}

        <HeroWrapper>
          <HeroSection>
            {brandParam === 'tgpay' && brand.logo && (
              <HeroLogo>
                <img src={brand.logo} alt={brand.name} />
              </HeroLogo>
            )}
            <HeroTitle>Merchant Onboarding</HeroTitle>
            <HeroSubtitle>{brand.description}</HeroSubtitle>
            <CtaButton onClick={handleGetStarted}>
              Start Your Application
            </CtaButton>
          </HeroSection>
        </HeroWrapper>

        <FeaturesSection>
          <FeaturesContainer>
            <FeaturesTitle>Why Choose {brand.name}?</FeaturesTitle>
            <FeaturesGrid>
              {brand.features.map((feature, index) => (
                <FeatureCard key={index}>
                  {brand.featureImages?.[index] && (
                    <img
                      className="feature-img"
                      src={brand.featureImages[index]}
                      alt={feature}
                    />
                  )}
                  <div className="feature-body">
                    {!brand.featureImages?.[index] && (
                      <div className="feature-icon">✓</div>
                    )}
                    <h3>{feature}</h3>
                    <p>Streamlined process to get you started quickly and efficiently.</p>
                  </div>
                </FeatureCard>
              ))}
            </FeaturesGrid>
          </FeaturesContainer>
        </FeaturesSection>

        <ComplianceSection>
          <ComplianceContainer>
            <div>
              <ComplianceLabel>Regulation 24 · MLR 2017</ComplianceLabel>
              <ComplianceTitle>AML Compliance Training</ComplianceTitle>
              <ComplianceDesc>
                An 8-week mandatory training programme covering money laundering,
                terrorist financing, and proliferation financing — built specifically
                for payments and acquiring teams.
              </ComplianceDesc>
              <CompliancePoints>
                <CompliancePoint>Tailored to payments, merchant onboarding, and acquiring</CompliancePoint>
                <CompliancePoint>MCQ knowledge checks + open-ended reflective questions</CompliancePoint>
                <CompliancePoint>Progress saved — complete at your own pace</CompliancePoint>
                <CompliancePoint>Manager review dashboard with full audit trail</CompliancePoint>
              </CompliancePoints>
              <ComplianceButton onClick={() => navigate('/training')}>
                Start Training Programme
              </ComplianceButton>
            </div>
            <ComplianceVisual>
              {[
                ['Week 1', 'AML Basics in Payments', true],
                ['Week 2', 'Regulation & Accountability', false],
                ['Week 3', 'Merchant Onboarding & KYB', false],
                ['Week 4', 'Transaction Monitoring', false],
                ['Week 5', 'Refunds & Settlement Risk', false],
                ['Week 6', 'Sanctions & Proliferation', false],
                ['Week 7', 'Escalation & Reporting', false],
                ['Week 8', 'Recordkeeping & Reflection', false],
              ].map(([week, title, active]) => (
                <WeekPill key={week as string} active={active as boolean}>
                  <span>{title as string}</span>
                  <WeekBadge>{week as string}</WeekBadge>
                </WeekPill>
              ))}
            </ComplianceVisual>
          </ComplianceContainer>
        </ComplianceSection>

        <FooterSection>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
            {brand.footerLogo ? (
              <img src={brand.footerLogo} alt={brand.name} style={{ height: '30px', maxWidth: '150px', objectFit: 'contain', marginRight: '10px' }} />
            ) : brand.logo ? (
              <img src={brand.logo} alt={brand.name} style={{ height: '30px', maxWidth: '150px', objectFit: 'contain', marginRight: '10px' }} />
            ) : null}
          </div>
          <p>&copy; {new Date().getFullYear()} {brand.name}. All rights reserved.</p>
          <p>
            Questions? Contact us at{' '}
            <a href={`mailto:${brand.contactEmail}`} style={{ color: brand.primaryColor }}>
              {brand.contactEmail}
            </a>
          </p>
        </FooterSection>
      </LandingContainer>
    </ThemeProvider>
  );
};

export default LandingPage;